export type SelectableOption = {
  value: string;
  disable?: boolean;
  [key: string]: any;
};

export type ModeWithAllowedValues = {
  resolution?: string[];
  [key: string]: any;
};

export type RowWithSelectableResolution = {
  resolution?: SelectableOption[];
  selectedResolution?: SelectableOption | null;
  selectedResolutionValue?: string | null;
  [key: string]: any;
};

export type ApplyModeConstraintsOptions<TMode> = {
  /**
   * Extracts the allowed resolution values from the selected mode.
   * Defaults to `mode.resolution`.
   */
  getAllowedValues?: (mode: TMode) => string[] | undefined;
};

/**
 * Generic helper: apply "allowed values" constraints to a row's `resolution` options.
 *
 * Behavior:
 * - Disables any option whose `value` is not in the allowed list.
 * - Clears selected resolution, then selects the first allowed value (or first enabled fallback).
 *
 * Returns true when constraints were applied (i.e., allowed list existed).
 */
export function applyResolutionConstraintsFromMode<TMode extends ModeWithAllowedValues>(
  row: RowWithSelectableResolution,
  productMode: TMode | TMode[],
  options: ApplyModeConstraintsOptions<TMode> = {},
): boolean {
  const mode = Array.isArray(productMode) ? productMode[0] : productMode;
  const getAllowedValues = options.getAllowedValues ?? ((m: TMode) => (m as any)?.resolution);
  const allowedValues = getAllowedValues(mode);
  const resolutionOptions = row?.resolution;

  const normalize = (value: string) => value?.trim()?.replace(/\s+/g, ' ');

  if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
    return false;
  }
  if (!Array.isArray(resolutionOptions) || resolutionOptions.length === 0) {
    return false;
  }

  const normalizedAllowedValues = allowedValues.map(normalize);
  const hasAnyMatch = resolutionOptions.some((option) =>
    normalizedAllowedValues.includes(normalize(option.value)),
  );
  if (!hasAnyMatch) {
    return false;
  }

  row.selectedResolution = null;
  row.selectedResolutionValue = null;

  for (const option of resolutionOptions) {
    option.disable = !normalizedAllowedValues.includes(normalize(option.value));
  }

  const preferredValue = normalize(allowedValues[0]);
  const preferred = resolutionOptions.find((r) => normalize(r.value) === preferredValue && r.disable !== true);
  const firstEnabled = preferred || resolutionOptions.find((r) => r.disable !== true);

  if (firstEnabled) {
    row.selectedResolution = firstEnabled;
    row.selectedResolutionValue = firstEnabled.value;
  }

  return true;
}
